import axios from 'axios';
import toast from 'react-hot-toast';
import { setupRequestInterceptor, setupResponseInterceptor } from './AuthInterceptorService';
import { getEnvironmentVariablesHandler } from 'Handlers/EnvHandler';
import logger from 'Services/MyTuiProfileCustomLogger';

const baseUrl = `${getEnvironmentVariablesHandler().apiBaseUrl}`;

const axiosInstance = axios.create({
  baseURL: baseUrl,
});

// Setup authentication interceptors
setupRequestInterceptor(axiosInstance);
setupResponseInterceptor(axiosInstance);

// Retry configuration for network errors
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableError = (err) => {
  // Retry only on network errors (no response received) that are not aborted
  return !err.response && err.request && err.code !== 'ECONNABORTED';
};

/**
 * Sanitize headers to prevent logging sensitive data (Bearer tokens)
 */
const sanitizeHeaders = (headers) => {
  if (!headers) return headers;
  const sanitized = { ...headers };
  if (sanitized.Authorization) {
    sanitized.Authorization = 'Bearer [REDACTED]';
  }
  return sanitized;
};

/**
 * Custom Error class for API errors
 */
class ApiError extends Error {
  constructor(message, status, originalError) {
    super(message);
    this.name = 'ApiError';
    this.status = status; // HTTP status code or null for network/setup errors
    this.originalError = originalError; // Keep original error for deeper inspection
  }
}

/**
 * Centralized error handler for API calls
 */
const handleError = (err) => {
  const currentTimeUTC = new Date().toISOString();
  let userMessage = 'An unexpected error occurred. Please try again later.'; // Default user message
  let logDetails = { time: currentTimeUTC };
  let statusCode = null;
  let rethrowError;

  if (err.response) {
    // Server responded with a status code outside the 2xx range
    statusCode = err.response.status;
    logDetails = {
      ...logDetails,
      type: 'Response Error',
      url: err.config?.url,
      method: err.config?.method,
      status: statusCode,
      statusText: err.response.statusText,
      requestHeaders: sanitizeHeaders(err.config?.headers),
      responseHeaders: err.response.headers,
    };
    logger.apiError('API Response Error:', err, logDetails);

    // Customize user message based on status code
    const responseMessage = err.response.data?.message || err.response.data?.developerMessage;

    switch (statusCode) {
      case 400: // Bad Request
        userMessage = responseMessage || 'Invalid request data. Please check your input.';
        break;
      case 401: // Unauthorized
        userMessage = 'Authentication failed. Please log in again.';
        // Consider triggering re-authentication flow if applicable
        break;
      case 403: // Forbidden
        userMessage = responseMessage || "You don't have permission to perform this action.";
        break;
      case 404: // Not Found
        userMessage = responseMessage || 'The requested resource was not found.';
        break;
      case 500: // Internal Server Error
      case 502: // Bad Gateway
      case 503: // Service Unavailable
      case 504: // Gateway Timeout
        userMessage = 'A server error occurred. Please try again later or contact support if the problem persists.';
        break;
      default:
        userMessage = responseMessage || `Request failed with status code ${statusCode}.`;
        break;
    }
    rethrowError = new ApiError(userMessage, statusCode, err);
  } else if (err.request) {
    // Request was made but no response received (Network Error, Timeout)
    logDetails = {
      ...logDetails,
      type: 'Request Error',
      url: err.config?.url,
      method: err.config?.method,
      headers: sanitizeHeaders(err.config?.headers),
      timeout: err.config?.timeout,
      message: 'No response received from server.',
    };
    logger.error('API Request Error:', err, logDetails);
    userMessage = 'Cannot connect to the server. Please check your network connection or try again later.';
    rethrowError = new ApiError(userMessage, null, err); // No status code available
  } else {
    // Distinguish auth errors from truly unknown errors
    const isAuthError = err.message?.includes('No account found') || err.message?.includes('Redirecting to login');

    if (isAuthError) {
      // Auth failures are not "unknown errors" — don't toast, don't log as error.
      // The MsalTokenService already handles the redirect flow.
      logger.warn('Auth session expired, user needs to re-login', { message: err.message });
      throw new ApiError(err.message, 401, err);
    }

    // Truly unknown errors
    logDetails = {
      ...logDetails,
      type: 'Setup/Unknown Error',
      message: err.message,
      stack: err.stack,
    };
    logger.error('API Unknown Error:', err, logDetails);
    userMessage = `An application error occurred. Please try again. (${err.message})`;
    rethrowError = new ApiError(userMessage, null, err);
  }

  // Show toast notification to the user
  // You could make this conditional, e.g., only show for 5xx or network errors
  toast.error(userMessage);

  // Re-throw the custom error object
  throw rethrowError;
};

class RestClient {
  static async Get(url, retries = MAX_RETRIES) {
    try {
      // Authorization header is automatically added by request interceptor
      const response = await axiosInstance.get(url);
      return response?.data;
    } catch (err) {
      if (isRetryableError(err) && retries > 0) {
        logger.warn(`Network error on GET ${url}, retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
        await sleep(RETRY_DELAY_MS);
        return RestClient.Get(url, retries - 1);
      }
      handleError(err); // handleError will throw an ApiError
    }
  }

  static async Post(url, data, retries = MAX_RETRIES) {
    try {
      // Authorization header is automatically added by request interceptor
      const response = await axiosInstance.post(url, data, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      });
      return response?.data;
    } catch (err) {
      if (isRetryableError(err) && retries > 0) {
        logger.warn(`Network error on POST ${url}, retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
        await sleep(RETRY_DELAY_MS);
        return RestClient.Post(url, data, retries - 1);
      }
      handleError(err);
    }
  }

  static async Patch(url, data, retries = MAX_RETRIES) {
    try {
      // Authorization header is automatically added by request interceptor
      const response = await axiosInstance.patch(url, data, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      });
      return response?.data;
    } catch (err) {
      if (isRetryableError(err) && retries > 0) {
        logger.warn(`Network error on PATCH ${url}, retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
        await sleep(RETRY_DELAY_MS);
        return RestClient.Patch(url, data, retries - 1);
      }
      handleError(err);
    }
  }

  static async Delete(url, data, retries = MAX_RETRIES) {
    try {
      // Authorization header is automatically added by request interceptor
      const config = {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        // Add data to body only if provided (uncommon for DELETE)
        ...(data && { data }),
      };
      const response = await axiosInstance.delete(url, config);
      return response?.data;
    } catch (err) {
      if (isRetryableError(err) && retries > 0) {
        logger.warn(`Network error on DELETE ${url}, retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
        await sleep(RETRY_DELAY_MS);
        return RestClient.Delete(url, data, retries - 1);
      }
      handleError(err);
    }
  }

  static async Put(url, data, retries = MAX_RETRIES) {
    try {
      // Authorization header is automatically added by request interceptor
      const response = await axiosInstance.put(url, data, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      });
      return response?.data;
    } catch (err) {
      if (isRetryableError(err) && retries > 0) {
        logger.warn(`Network error on PUT ${url}, retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
        await sleep(RETRY_DELAY_MS);
        return RestClient.Put(url, data, retries - 1);
      }
      handleError(err);
    }
  }

  static async Upload(url, file, data = []) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      data.forEach((item) => {
        formData.append(item.key, item.value);
      });

      // Authorization header is automatically added by request interceptor
      // Content-Type: multipart/form-data is set automatically by browser/axios for FormData
      const response = await axiosInstance.post(url, formData);
      return response?.data;
    } catch (err) {
      // No retry for uploads — they can be large and non-idempotent
      handleError(err);
    }
  }
}

export default RestClient;
